/**
 * @author      yewon@zumper.com (Yewon Lee)
 * @copyright   Copyright (c) 2018, Zumper
 * @description handle analytics and butter action after failing to send a message
 */

import { onMessageSent } from '@zcode-ui/web-zumper-global-state'
import { isCookieError, isInvalidEmailDomainError } from '@zcode/error-filters'
import { trackGAEvent } from '@zcode/nr-utils'
import type { MessageData, SendMessageArgs } from '@zcode/state-type'
import { logger } from '../../../../logger'
import {
  ACCOUNT_EXISTS_ERROR_MSG,
  CONNECTION_SEND_ERROR_MSG,
  CONTACT_SUPPORT_SEND_ERROR_MSG,
  DEFAULT_SEND_ERROR_MSG,
  INVALID_DATE_SEND_ERROR_MSG,
  INVALID_EMAIL_DOMAIN_ERROR_MSG,
  REFRESH_SEND_ERROR_MSG,
  REPLAY_SEND_ERROR_MSG,
  RETRY_SEND_ERROR_MSG,
  UNAUTHORIZED_SEND_ERROR_MSG,
} from '../../../constants/message'
import type { Dispatch } from '../../../types'
import * as Analytics from '../../../utils/analytics'
import { handleButter } from './handleButter'
import { has } from './has'
import refineMessage from './refineMessage'
import type { Message } from './setupMessageQuery'

export const API_RESPONSE = {
  // api error response
  CSRF: 'CSRF cookie not set.',
  EMAIL_BLOCKED: 'email is blocked',
  INVALID_DATE: 'is before 1900; the datetime strftime() methods require year >= 1900',
  INVALID_EMAIL_DOMAIN: 'invalid email domain',
  NOT_MESSAGEABLE: 'The building is not messageable at this time',
  OTHER_USER: 'The email you have entered belongs to an account. Please login to send a message.',
  REFERRER: 'Referer checking failed - no Referer.',
  REPLAY: 'replay',
  SERVER_UNAVAILABLE: 'Server Unavailable',
  TIMED_OUT: 'timed out',
} as const

interface HandleErrorAnalyticsAndButterProps {
  config: {
    analyticChannel: SendMessageArgs['analyticChannel']
    isMulti: boolean | undefined
  }
  err: unknown
  message: Message
  messageData: MessageData
}

const handleErrorAnalyticsAndButter =
  ({ config, err, message, messageData }: HandleErrorAnalyticsAndButterProps) =>
  (dispatch: Dispatch) => {
    const { analyticChannel, isMulti } = config
    const { completionEvtName, feedNames, originalTarget } = messageData

    // message send failed
    logger.warn(message.listing_ids, err)

    // dispatch that message failed to send
    dispatch(onMessageSent({ success: false }))
    // multi message tracking
    if (isMulti) {
      Analytics.trackMessageCompletion({
        target: originalTarget,
        result: false,
        num: feedNames?.length ?? 0,
        evt: completionEvtName,
      })
    } else {
      // single message tracking
      Analytics.trackMessageCompletion({
        target: messageData,
        result: false,
      })
    }
    // common messaging human-condition errors
    if (has(err, 'status') && err.status === 451) {
      if (has(err, 'data') && has(err.data, 'field') && err.data.field) {
        dispatch(
          handleButter({
            msg: refineMessage(err),
            duration: 5000,
          })
        )
        Analytics.trackFieldError(
          err.data.field,
          has(err.data, 'reason') ? err.data.reason : undefined
        )
      }
    } else if (
      has(err, 'status') &&
      typeof err.status === 'number' &&
      err.status >= 400 &&
      has(err, 'data') &&
      has(err.data, 'reason') &&
      typeof err.data.reason === 'string'
    ) {
      if (err.data.reason === API_RESPONSE.REPLAY) {
        dispatch(
          handleButter({
            msg: REPLAY_SEND_ERROR_MSG,
            duration: 5000,
          })
        )
      } else if (
        has(err, 'data') &&
        has(err.data, 'reason') &&
        err.data.reason === API_RESPONSE.OTHER_USER
      ) {
        dispatch(
          handleButter({
            msg: UNAUTHORIZED_SEND_ERROR_MSG,
            duration: 5000,
          })
        )
      } else if (isCookieError(err)) {
        dispatch(
          handleButter({
            duration: 5000,
            msg: 'We require cookies to send messages. Please enable your cookies and try again.',
          })
        )
      } else if (
        has(err, 'data') &&
        has(err.data, 'reason') &&
        (err.data.reason === API_RESPONSE.CSRF || err.data.reason === API_RESPONSE.REFERRER)
      ) {
        dispatch(
          handleButter({
            duration: 5000,
            msg: REFRESH_SEND_ERROR_MSG,
          })
        )
      } else if (
        has(err, 'data') &&
        has(err.data, 'reason') &&
        err.data.reason === API_RESPONSE.TIMED_OUT
      ) {
        dispatch(
          handleButter({
            msg: CONNECTION_SEND_ERROR_MSG,
            duration: 5000,
          })
        )
      } else if (
        has(err, 'data') &&
        has(err.data, 'reason') &&
        err.data.reason === API_RESPONSE.NOT_MESSAGEABLE
      ) {
        dispatch(
          handleButter({
            msg: API_RESPONSE.NOT_MESSAGEABLE,
            duration: 5000,
          })
        )
      } else if (
        has(err, 'data') &&
        has(err.data, 'reason') &&
        err.data.reason.includes(API_RESPONSE.INVALID_DATE)
      ) {
        dispatch(
          handleButter({
            msg: INVALID_DATE_SEND_ERROR_MSG,
            duration: 5000,
          })
        )
      } else if (
        has(err, 'data') &&
        has(err.data, 'reason') &&
        err.data.reason === API_RESPONSE.EMAIL_BLOCKED
      ) {
        dispatch(
          handleButter({
            entity: messageData,
            msg: CONTACT_SUPPORT_SEND_ERROR_MSG,
            duration: 5000,
            report: true,
          })
        )
      } else if (has(err, 'data') && isInvalidEmailDomainError(err.data)) {
        dispatch(
          handleButter({
            msg: INVALID_EMAIL_DOMAIN_ERROR_MSG,
            duration: 5000,
          })
        )
      } else if (
        has(err, 'data') &&
        has(err.data, 'reason') &&
        err.data.reason === API_RESPONSE.SERVER_UNAVAILABLE
      ) {
        dispatch(
          handleButter({
            msg: RETRY_SEND_ERROR_MSG,
            duration: 5000,
          })
        )
      }
    } else if (has(err, 'status') && err.status === 450) {
      dispatch(
        handleButter({
          msg: ACCOUNT_EXISTS_ERROR_MSG,
          duration: 5000,
        })
      )
    } else {
      logger.error('message.send failed', err)
      dispatch(
        handleButter({
          msg: DEFAULT_SEND_ERROR_MSG,
          duration: 5000,
        })
      )
    }
    trackGAEvent(analyticChannel, 'MessageSend', 'Failure')
  }

export default handleErrorAnalyticsAndButter
